import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  Briefcase, Plus, MessageCircle, X, Send, User, Building, ExternalLink, Clock,
  AlertCircle, CheckCircle2, Zap, Trash2, Target,
} from "lucide-react";

const STATUS_PILL = {
  backlog:     "bg-neutral-100 border-neutral-300 text-neutral-700",
  assigned:    "bg-blue-50 border-blue-300 text-blue-800",
  in_progress: "bg-amber-50 border-amber-300 text-amber-800",
  review:      "bg-purple-50 border-purple-300 text-purple-800",
  completed:   "bg-emerald-50 border-emerald-300 text-emerald-800",
  cancelled:   "bg-neutral-200 border-neutral-400 text-neutral-500 line-through",
};

const PRIORITY_ICON = {
  low:    { icon: Clock, color: "text-neutral-400" },
  normal: { icon: Clock, color: "text-neutral-500" },
  high:   { icon: AlertCircle, color: "text-amber-600" },
  urgent: { icon: Zap, color: "text-rose-600" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}

export default function Jobs() {
  const [rows, setRows] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openJob, setOpenJob] = useState(null);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({
    title: "", description: "", contractor_id: "", client_id: "",
    source: "internal", priority: "normal", due_date: "", budget: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: j }, { data: co }, { data: cl }] = await Promise.all([
        api.get("/jobs"),
        api.get("/contractors"),
        api.get("/clients"),
      ]);
      setRows(j.jobs || []);
      setContractors(co.contractors || []);
      setClients(cl.clients || cl || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", {
        ...form,
        client_id: form.client_id || null,
        contractor_id: form.contractor_id || null,
        due_date: form.due_date || null,
        budget: form.budget ? parseFloat(form.budget) : null,
      });
      toast.success("Job created.");
      setCreating(false);
      setForm({ title: "", description: "", contractor_id: "", client_id: "", source: "internal", priority: "normal", due_date: "", budget: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/jobs/${id}`, body);
      load();
      if (openJob?.id === id) {
        const { data } = await api.get(`/jobs/${id}`);
        setOpenJob(data);
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setOpenJob(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const postMsg = async (body) => {
    if (!openJob) return;
    try {
      await api.post(`/jobs/${openJob.id}/messages`, { body });
      const { data } = await api.get(`/jobs/${openJob.id}`);
      setOpenJob(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = rows.filter((r) => filter === "all" ? true : r.status === filter);
  const kpis = {
    total: rows.length,
    active: rows.filter((r) => ["assigned", "in_progress", "review"].includes(r.status)).length,
    completed: rows.filter((r) => r.status === "completed").length,
    urgent: rows.filter((r) => r.priority === "urgent" && r.status !== "completed").length,
  };

  return (
    <div className="p-8 max-w-7xl" data-testid="jobs-page">
      <Toaster position="top-right" richColors />
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Briefcase className="w-3.5 h-3.5" /> Jobs · Work orders</div>
          <h1 className="text-3xl font-bold text-neutral-900">Delivery pipeline</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl text-sm">
            Every unit of work — client-paid or internal — routed to a contractor.
            Threaded messages, status timeline, and JOB-XXXX numbering per job.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="lf-btn-dark text-sm inline-flex items-center gap-2" data-testid="jobs-new-btn">
          <Plus className="w-4 h-4" /> New job
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Total jobs" value={kpis.total} />
        <Kpi label="Active" value={kpis.active} accent="amber" />
        <Kpi label="Completed" value={kpis.completed} accent="emerald" />
        <Kpi label="Urgent open" value={kpis.urgent} accent="rose" />
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", "backlog", "assigned", "in_progress", "review", "completed"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`jobs-filter-${k}`}
            className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border ${filter === k ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-300 hover:border-neutral-900"}`}
          >
            {k}
          </button>
        ))}
      </div>

      {loading ? <div className="text-neutral-500 text-sm">Loading…</div> :
       filtered.length === 0 ? (
         <div className="border border-dashed border-neutral-300 bg-white p-16 text-center">
           <Briefcase className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
           <div className="font-display text-xl mb-1">No jobs {filter !== "all" ? `in "${filter}"` : "yet"}</div>
           <p className="text-neutral-500 text-sm">Create a job to route work to a contractor.</p>
         </div>
       ) : (
        <div className="space-y-3">
          {filtered.map((j) => {
            const PI = PRIORITY_ICON[j.priority] || PRIORITY_ICON.normal;
            const pill = STATUS_PILL[j.status] || STATUS_PILL.backlog;
            return (
              <div key={j.id} className="bg-white border border-neutral-200 hover:border-neutral-400 p-4 cursor-pointer" onClick={() => openDetail(j.id)} data-testid={`job-${j.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono uppercase tracking-widest text-neutral-400">{j.job_number}</span>
                      <span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 border ${pill}`}>{j.status}</span>
                      <PI.icon className={`w-3.5 h-3.5 ${PI.color}`} title={j.priority} />
                      <span className="text-xs text-neutral-500 font-mono">{j.priority}</span>
                    </div>
                    <div className="font-display text-lg font-bold mt-1">{j.title}</div>
                    <div className="text-sm text-neutral-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />
                        {j.contractor ? <><strong className="text-neutral-900">{j.contractor.name}</strong> · {j.contractor.contractor_number || "unassigned"}</> : <span className="italic">unassigned</span>}
                      </span>
                      <span className="inline-flex items-center gap-1"><Building className="w-3 h-3" />
                        {j.client?.business_name || <span className="italic">{j.source === "internal" ? "internal" : "no client"}</span>}
                      </span>
                      {j.due_date && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> due {fmt(j.due_date)}</span>}
                      {j.budget && <span className="font-mono text-xs">${j.budget.toLocaleString()}</span>}
                      <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {j.message_count || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
       )
      }

      {creating && (
        <div onClick={() => setCreating(false)} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-white w-full max-w-lg border border-neutral-900 shadow-2xl">
            <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between">
              <div><div className="text-xs font-mono uppercase tracking-widest text-amber-300">New job</div><div className="text-xl font-bold mt-1">Route work to a contractor</div></div>
              <button type="button" onClick={() => setCreating(false)} className="text-neutral-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid gap-3 text-sm">
              <label className="grid gap-1"><span className="overline">Title · required</span><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border border-neutral-300 px-3 py-2" data-testid="job-form-title" /></label>
              <label className="grid gap-1"><span className="overline">Description</span><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-neutral-300 px-3 py-2 text-sm" /></label>
              <div className="grid grid-cols-2 gap-2">
                <label className="grid gap-1"><span className="overline">Source</span>
                  <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="border border-neutral-300 px-3 py-2">
                    <option value="internal">Internal (ClientsOnRamp)</option>
                    <option value="client">Client-paid</option>
                  </select>
                </label>
                <label className="grid gap-1"><span className="overline">Priority</span>
                  <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="border border-neutral-300 px-3 py-2">
                    <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                  </select>
                </label>
              </div>
              <label className="grid gap-1"><span className="overline">Contractor</span>
                <select value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })} className="border border-neutral-300 px-3 py-2" data-testid="job-form-contractor">
                  <option value="">— assign later —</option>
                  {contractors.filter((c) => c.status !== "inactive").map((c) => <option key={c.id} value={c.id}>{c.contractor_number || ""} · {c.name}</option>)}
                </select>
              </label>
              <label className="grid gap-1"><span className="overline">Client · optional</span>
                <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="border border-neutral-300 px-3 py-2">
                  <option value="">— no linked client —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="grid gap-1"><span className="overline">Due date</span><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="border border-neutral-300 px-3 py-2" /></label>
                <label className="grid gap-1"><span className="overline">Budget $</span><input type="number" min="0" step="1" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="border border-neutral-300 px-3 py-2" /></label>
              </div>
              <button type="submit" className="bg-neutral-900 text-white px-4 py-2.5 font-semibold hover:bg-neutral-700" data-testid="job-form-submit">Create job</button>
            </div>
          </form>
        </div>
      )}

      {openJob && <JobDetailDrawer job={openJob} onClose={() => setOpenJob(null)} onPatch={(body) => patch(openJob.id, body)} onMessage={postMsg} contractors={contractors} />}
    </div>
  );
}

function JobDetailDrawer({ job, onClose, onPatch, onMessage, contractors }) {
  const [msg, setMsg] = useState("");
  const [milestones, setMilestones] = useState(null);
  const [newMs, setNewMs] = useState({ title: "", target_date: "" });

  const loadMs = async () => {
    try {
      const { data } = await api.get(`/jobs/${job.id}/milestones`);
      setMilestones(data);
    } catch { /* noop */ }
  };
  useEffect(() => { loadMs(); }, [job.id]);

  const submit = (e) => { e.preventDefault(); if (!msg.trim()) return; onMessage(msg.trim()); setMsg(""); };

  const addMs = async (e) => {
    e.preventDefault();
    if (!newMs.title.trim()) return;
    try {
      await api.post(`/jobs/${job.id}/milestones`, { title: newMs.title.trim(), target_date: newMs.target_date || null });
      setNewMs({ title: "", target_date: "" });
      loadMs();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const toggleMs = async (m) => {
    try {
      await api.patch(`/jobs/${job.id}/milestones/${m.id}`, { completed: !m.completed_at });
      loadMs();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const delMs = async (m) => {
    if (!window.confirm("Delete this milestone?")) return;
    try {
      await api.delete(`/jobs/${job.id}/milestones/${m.id}`);
      loadMs();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-black/60 flex justify-end p-0" data-testid="job-detail-drawer">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl h-full overflow-y-auto border-l border-neutral-900">
        <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between sticky top-0 z-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-amber-300">{job.job_number}</div>
            <div className="text-xl font-bold mt-1">{job.title}</div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 grid gap-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <label className="grid gap-1"><span className="overline">Status</span>
              <select value={job.status} onChange={(e) => onPatch({ status: e.target.value })} className="border border-neutral-300 px-3 py-2" data-testid="job-status-select">
                {["backlog", "assigned", "in_progress", "review", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="grid gap-1"><span className="overline">Contractor</span>
              <select value={job.contractor_id || ""} onChange={(e) => onPatch({ contractor_id: e.target.value || null })} className="border border-neutral-300 px-3 py-2">
                <option value="">— unassigned —</option>
                {contractors.filter((c) => c.status !== "inactive").map((c) => <option key={c.id} value={c.id}>{c.contractor_number || ""} · {c.name}</option>)}
              </select>
            </label>
          </div>
          {job.description && <div className="border-l-2 border-neutral-300 pl-4 text-sm text-neutral-700 whitespace-pre-wrap">{job.description}</div>}
          <div className="grid grid-cols-3 gap-2 text-sm border-t border-neutral-100 pt-4">
            <div><div className="overline">Source</div><strong>{job.source}</strong></div>
            <div><div className="overline">Priority</div><strong>{job.priority}</strong></div>
            <div><div className="overline">Budget</div><strong>{job.budget ? `$${job.budget.toLocaleString()}` : "—"}</strong></div>
            <div><div className="overline">Due</div><strong>{fmt(job.due_date)}</strong></div>
            <div><div className="overline">Assigned</div><strong>{fmt(job.assigned_at)}</strong></div>
            <div><div className="overline">Started</div><strong>{fmt(job.started_at)}</strong></div>
          </div>
          {job.contractor && (
            <div className="border border-neutral-200 p-3 text-sm bg-neutral-50">
              <div className="overline mb-1">Assigned contractor</div>
              <strong>{job.contractor.name}</strong> · <span className="font-mono text-xs text-neutral-500">{job.contractor.contractor_number}</span>
              <div className="text-neutral-500 text-xs mt-1">{job.contractor.email}</div>
            </div>
          )}

          {/* Milestones */}
          <div className="border-t border-neutral-100 pt-4" data-testid="job-milestones">
            <div className="overline mb-2 flex items-center gap-2">
              <Target className="w-3 h-3" /> Milestones
              {milestones && <span className="ml-auto font-mono text-neutral-500">{milestones.done}/{milestones.total} · {milestones.progress}%</span>}
            </div>
            {milestones && milestones.total > 0 && (
              <div className="h-2 bg-neutral-100 mb-3 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${milestones.progress}%` }} />
              </div>
            )}
            <div className="space-y-2 mb-3">
              {(milestones?.milestones || []).map((m) => (
                <div key={m.id} className={`flex items-start gap-2 border ${m.completed_at ? "border-emerald-200 bg-emerald-50" : "border-neutral-200 bg-white"} p-2`}>
                  <button onClick={() => toggleMs(m)} className="mt-0.5" data-testid={`milestone-toggle-${m.id}`}>
                    <CheckCircle2 className={`w-5 h-5 ${m.completed_at ? "text-emerald-600" : "text-neutral-300 hover:text-neutral-500"}`} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${m.completed_at ? "line-through text-neutral-500" : ""}`}>{m.title}</div>
                    <div className="text-xs text-neutral-500 font-mono">
                      {m.target_date && <>target {fmt(m.target_date)}</>}
                      {m.completed_at && <> · done {fmt(m.completed_at)}{m.completed_by ? ` by ${m.completed_by}` : ""}</>}
                    </div>
                  </div>
                  <button onClick={() => delMs(m)} className="text-neutral-300 hover:text-rose-500" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {milestones && milestones.total === 0 && (
                <div className="text-xs text-neutral-400 italic">No milestones yet. Add one below to track timelines.</div>
              )}
            </div>
            <form onSubmit={addMs} className="flex gap-2">
              <input value={newMs.title} onChange={(e) => setNewMs({ ...newMs, title: e.target.value })} placeholder="New milestone…" className="border border-neutral-300 px-3 py-2 flex-1 text-sm" data-testid="milestone-title-input" />
              <input type="date" value={newMs.target_date} onChange={(e) => setNewMs({ ...newMs, target_date: e.target.value })} className="border border-neutral-300 px-3 py-2 text-sm" />
              <button type="submit" className="lf-btn-dark text-sm px-3 py-2 flex items-center gap-1" data-testid="milestone-add-btn"><Plus className="w-3 h-3" /> Add</button>
            </form>
          </div>

          <div className="border-t border-neutral-100 pt-4">
            <div className="overline mb-3 flex items-center gap-2"><MessageCircle className="w-3 h-3" /> Messages ({(job.messages || []).length})</div>
            <div className="space-y-2 max-h-96 overflow-y-auto mb-3">
              {(job.messages || []).length === 0 && <div className="text-sm text-neutral-400 italic">No messages yet. Kick off the thread below.</div>}
              {(job.messages || []).map((m) => (
                <div key={m.id} className="bg-neutral-50 border border-neutral-200 p-3">
                  <div className="text-xs font-mono text-neutral-500 mb-1">{m.author_name} · {new Date(m.created_at).toLocaleString()}</div>
                  <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                </div>
              ))}
            </div>
            <form onSubmit={submit} className="flex gap-2">
              <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Add a message…" className="border border-neutral-300 px-3 py-2 flex-1 text-sm" data-testid="job-msg-input" />
              <button type="submit" className="lf-btn-dark text-sm px-3 py-2 flex items-center gap-1" data-testid="job-msg-submit"><Send className="w-3 h-3" /> Send</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  const map = { emerald: "border-emerald-200 bg-emerald-50", amber: "border-amber-200 bg-amber-50", rose: "border-rose-200 bg-rose-50" };
  return (
    <div className={`border p-4 ${map[accent] || "border-neutral-200 bg-white"}`}>
      <div className="overline">{label}</div>
      <div className="text-2xl font-bold text-neutral-900 mt-1.5">{value ?? "—"}</div>
    </div>
  );
}
